import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { FaShoppingCart, FaTrash, FaEye, FaArrowLeft, FaAtlas, FaCheck, FaGraduationCap, FaLayerGroup } from 'react-icons/fa';
import Alert from '../components/Alert';

const Cart = () => {
  const { cart, removeFromCart, clearCart, completed } = useCart();
  const [alertState, setAlertState] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const showAlert = (type, title, message, onConfirm) => {
    setAlertState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm: () => {
        if (onConfirm) onConfirm();
        setAlertState(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleRemoveItem = (itemId) => {
    showAlert(
      'warning',
      'Remove Resource',
      'Are you sure you want to remove this resource from your library?',
      () => removeFromCart(itemId)
    );
  };

  const handleCompleteItem = (itemId) => {
    showAlert(
      'success',
      'Mark as Completed',
      'Mark this resource as completed?',
      () => {
        completed(itemId)
        console.log('Marked as completed:', itemId);
      }
    );
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#f3f4f6]" style={{
          backgroundImage: `
            radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
            radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
          `
        }}>
        <div className="min-h-screen flex items-center justify-center p-4">
            <div className="text-center max-w-lg mx-auto bg-white/60 backdrop-blur-xl p-12 rounded-3xl border border-white/50 shadow-xl">
            <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <FaLayerGroup className="w-10 h-10 text-indigo-500" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Your library is empty</h2>
            <p className="text-gray-600 mb-8 text-lg font-light leading-relaxed">
                Unlock your potential by adding high-quality resources to your personal learning library.
            </p>
            <Link
                to="/resource-hub"
                className="inline-flex items-center px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 hover:shadow-xl hover:-translate-y-0.5 transition-all group"
            >
                <FaArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                Browse Resources
            </Link>
            </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#f3f4f6] pt-24 pb-12 px-4 sm:px-6 lg:px-8" style={{
        backgroundImage: `
            radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
            radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
        `
      }}>
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="mb-10 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row items-center sm:items-end justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/80 backdrop-blur-md rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm border border-white/50">
                        <FaGraduationCap className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Your Library</h1>
                        <p className="text-gray-500 font-medium mt-1">{cart.length} item{cart.length !== 1 ? 's' : ''} saved for later</p>
                    </div>
                </div>
                
                <Link to="/resource-hub" className="text-indigo-600 hover:text-indigo-700 font-bold text-sm bg-white/50 px-4 py-2 rounded-full border border-white/60 hover:bg-white transition flex items-center">
                    <FaArrowLeft className="mr-2" /> Continue Browsing
                </Link>
            </div>
          </div>

          <div className="grid gap-6">
            {cart.map((item, index) => (
              <div
                key={item.resourceId}
                className="group relative bg-white/70 backdrop-blur-xl rounded-3xl shadow-lg border border-white/60 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:bg-white/80 hover:-translate-y-1"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                 {/* Premium Corner Accent */}
                 <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-indigo-500/10 to-transparent rounded-bl-[4rem]"></div>

                <div className="p-6">
                  <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
                    {/* Image */}
                    <div className="w-full md:w-48 h-32 md:h-32 rounded-2xl overflow-hidden shadow-md flex-shrink-0 group-hover:shadow-lg transition-all relative">
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors z-10"></div>
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                        />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                          <span className="px-3 py-1 bg-indigo-50/80 text-indigo-700 text-xs font-bold uppercase tracking-wider rounded-lg border border-indigo-100">
                              {item.category || 'Resource'}
                          </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 leading-tight group-hover:text-indigo-700 transition-colors">
                          {item.title}
                      </h3>
                      
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed max-w-2xl">
                          {item.description}
                      </p>
                      
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row md:flex-col gap-3 w-full md:w-auto mt-4 md:mt-0 flex-shrink-0">
                      <Link
                        to={`/resource/${item.resourceId}`}
                        className="flex-1 md:flex-none inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-indigo-600 shadow-md hover:shadow-lg transition-all text-sm"
                      >
                        <FaEye className="w-4 h-4 mr-2" /> View
                      </Link>
                      
                      <button
                        onClick={() => handleCompleteItem(item.resourceId)}
                        className="flex-1 md:flex-none inline-flex items-center justify-center px-6 py-2.5 bg-white text-green-600 border border-green-200 font-semibold rounded-xl hover:bg-green-50 transition-all text-sm group/btn"
                      >
                        <FaCheck className="w-3.5 h-3.5 mr-2 group-hover/btn:scale-110 transition-transform" /> Done
                      </button>
                      
                      <button
                        onClick={() => handleRemoveItem(item.resourceId)}
                        className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 text-red-500 bg-transparent hover:bg-red-50 font-medium rounded-xl transition-all text-sm"
                      >
                        <FaTrash className="w-3.5 h-3.5 mr-2" /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <Alert
        isOpen={alertState.isOpen}
        onClose={() => setAlertState(prev => ({ ...prev, isOpen: false }))}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.type === 'warning' ? 'Remove' : 'Confirm'}
        className="backdrop-blur-xl bg-white/90"
      />
    </>
  );
};

export default Cart;